import { Box, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { BidDirection } from '@/features/bidding/types/bidLadderInput'
import type { ForecastMarketPair, PriceQuantile } from '@/features/bidding/types/forecastPricing'
import { MARKET_PAIR_OPTIONS, QUANTILE_OPTIONS } from '@/features/bidding/types/forecastPricing'

type BidLadderForecastChipProps = {
  market: ForecastMarketPair
  quantile: PriceQuantile
  direction: BidDirection
  /** Whether this chip should have focus styling (for configuration tab interaction) */
  isFocused?: boolean
  onClick?: () => void
}

function getMarketShortLabel(market: ForecastMarketPair): string {
  const option = MARKET_PAIR_OPTIONS.find((o) => o.value === market)
  return option?.shortLabel ?? market
}

function getQuantileLabel(quantile: PriceQuantile): string {
  const option = QUANTILE_OPTIONS.find((o) => o.value === quantile)
  return option?.label ?? quantile.toUpperCase()
}

function BidLadderForecastChip({
  market,
  quantile,
  direction,
  isFocused = false,
  onClick,
}: Readonly<BidLadderForecastChipProps>) {
  const { t } = useTranslation()
  const color = direction === 'up' ? 'success' : 'error'

  const marketShortLabel = getMarketShortLabel(market)
  const quantileLabel = getQuantileLabel(quantile)
  const chipLabel = `${marketShortLabel} ${quantileLabel}`
  const tooltipText = t('bidding.bidding_cascading.bid_ladder.forecast_chip_tooltip', {
    market: marketShortLabel,
    quantile: quantileLabel,
  })

  return (
    <Tooltip arrow placement={direction === 'up' ? 'right' : 'left'} title={tooltipText}>
      <Box
        sx={{
          width: { xs: 90, sm: 80 },
          height: { xs: 32, sm: 28 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: direction === 'down' ? 'flex-end' : 'flex-start',
          px: 0.75,
          backgroundColor: isFocused ? `${color}.50` : `${color}.main`,
          borderRadius: '4px',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
          border: '1px solid',
          borderColor: isFocused ? `${color}.main` : 'transparent',
          boxShadow: isFocused ? 1 : 0,
          '&:hover': onClick
            ? {
                backgroundColor: `${color}.dark`,
                borderColor: `${color}.dark`,
              }
            : {},
        }}
        onClick={onClick}
      >
        <Typography
          sx={{
            fontSize: '0.5625rem',
            fontWeight: 600,
            color: isFocused ? `${color}.main` : 'common.white',
            textTransform: 'uppercase',
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {chipLabel}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export default BidLadderForecastChip
